import {
  type CoordinateFormat,
  defaultCoordinateFormat,
  formatCoordinatePair,
  formatCoordinateValue,
  parseCoordinateValue,
} from '@gmn-portal/backend';
import type { CoordinateValue, Position } from '@/shared/schema';

export type EditableCoordinateValue = {
  latitude: string;
  longitude: string;
};

export type EditablePosition = {
  id: string;
  label?: string;
  latitude: string;
  longitude: string;
  status?: boolean;
};

export const getPreferredCoordinateFormat = (value?: CoordinateFormat | null): CoordinateFormat => {
  return value ?? defaultCoordinateFormat;
};

export const toEditableCoordinate = (
  value: Partial<CoordinateValue> | null | undefined,
  format: CoordinateFormat
): EditableCoordinateValue => {
  return {
    latitude:
      value?.latitude === undefined || value.latitude === null
        ? ''
        : formatCoordinateValue(value.latitude, 'latitude', format),
    longitude:
      value?.longitude === undefined || value.longitude === null
        ? ''
        : formatCoordinateValue(value.longitude, 'longitude', format),
  };
};

export const toEditablePosition = (
  value: Partial<Position> | null | undefined,
  format: CoordinateFormat
): EditablePosition => {
  const coordinate = toEditableCoordinate(value, format);

  return {
    id: value?.id ?? '',
    label: value?.label ?? '',
    latitude: coordinate.latitude,
    longitude: coordinate.longitude,
    status: value?.status,
  };
};

export const toStoredCoordinate = (
  value: Partial<EditableCoordinateValue> | null | undefined
): CoordinateValue | undefined => {
  const latitude = parseCoordinateValue(value?.latitude, 'latitude');
  const longitude = parseCoordinateValue(value?.longitude, 'longitude');

  if (latitude === null || longitude === null) {
    return undefined;
  }

  return { latitude, longitude };
};

export const toStoredPosition = (
  value: Partial<EditablePosition> | null | undefined
): Position | undefined => {
  if (!value?.id) return undefined;

  const coordinate = toStoredCoordinate(value);
  return {
    id: value.id,
    label: value.label ?? '',
    latitude: coordinate?.latitude ?? Number.NaN,
    longitude: coordinate?.longitude ?? Number.NaN,
    ...(value.status !== undefined ? { status: value.status } : {}),
  };
};

export const formatCoordinateForCopy = (
  value: Partial<CoordinateValue> | null | undefined,
  format: CoordinateFormat
): string => {
  const latitude = parseCoordinateValue(value?.latitude, 'latitude');
  const longitude = parseCoordinateValue(value?.longitude, 'longitude');
  if (latitude === null || longitude === null) return '';
  return formatCoordinatePair({ latitude, longitude }, format);
};

export const formatStoredCoordinatePart = (
  value: number | string | undefined,
  axis: 'latitude' | 'longitude',
  format: CoordinateFormat
): string => {
  if (value === undefined) return '';
  return formatCoordinateValue(value, axis, format);
};

export const tryParseCoordinatePair = (input: string): [string, string] | null => {
  const trimmed = input.trim();
  const normalized = trimmed
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201C\u201D\u2033]/g, '"');

  const decimalPattern = /^(-?\d+[.,]?\d*)\s*,\s*(-?\d+[.,]?\d*)$/;
  const decimalMatch = normalized.match(decimalPattern);
  if (decimalMatch) {
    return [decimalMatch[1].trim(), decimalMatch[2].trim()];
  }

  const degreeCommaPattern = /^(.+['"](?:\s*\d+(?:[.,]\d+)?")?)\s*,\s*(\d.*)$/;
  const degreeCommaMatch = normalized.match(degreeCommaPattern);
  if (degreeCommaMatch) {
    return [degreeCommaMatch[1].trim(), degreeCommaMatch[2].trim()];
  }

  const degreeSpacePattern = /^(.+['"](?:\s*\d+(?:[.,]\d+)?")?)\s+(\d.*)$/;
  const degreeSpaceMatch = normalized.match(degreeSpacePattern);
  if (degreeSpaceMatch) {
    return [degreeSpaceMatch[1].trim(), degreeSpaceMatch[2].trim()];
  }

  return null;
};

export const getCoordinatePlaceholder = (
  _axis: 'latitude' | 'longitude',
  _format: CoordinateFormat
): string => {
  return `00°00'00.00"`;
};
